import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductService } from "../../../Services/product.service";
import { Category } from "../../../Models/category";
import { CategoryService } from "../../../Services/category.service";
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Subject,
  skip,
  Subscription,
  switchMap,
  take,
  takeUntil,
  timer,
} from "rxjs";
import { ProductConfigurationService } from "src/app/Services/product-configuration.service";
import { BrandService } from "src/app/Services/brand.service";
import { Brand } from "src/app/Models/brand";
import { ContactInformations } from "src/app/Models/Contact-informations";
import { MensualPipe } from "src/app/shared/pipes/mensual.pipe";
import { AccountService } from "src/app/Services/account.service";
import { CartService } from "src/app/Services/cart.service";
import { CartOffLineService } from "src/app/Services/cartOffline.service";
import { FavoriteService } from "src/app/Services/favorite.service";
import { CartProduct } from "src/app/Models/cart-product";
import { SeoService } from "src/app/Services/seo.service";
import { ProductConfiguration } from "src/app/Models/product-configuration";
import { cartLocal } from "../detail-page/detail-page.component";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "src/app/Services/language.service";
import { TimedSnackbar } from "src/app/shared/utils/timed-snackbar";
import { homeCrumb } from "src/app/shared/utils/breadcrumb";

@Component({
  selector: "app-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
  standalone: false
})
export class ProductListComponent implements OnInit, OnDestroy {
  @ViewChild("productCatalogue") productCatalogue?: {
    onSearchInput: (query: string) => void;
  };

  /**list of product categories */
  categoriesList!: any[];

  /**
   * structured categories list by hierarchy (cat, sub and sub-sub) to be used in displaying list of categories
   */
  structuredCategoriesList: any[] = [];

  /**list of products */
  productsList!: any[];

  /**minimum price to filter by*/
  minPrice: number = 0;

  /**maximum price to filter by*/
  maxPrice: number = 1000000;
  /**
   * current selected category
   */
  currentCategory: { id: number; name: string } = { id: 0, name: "" };

  /**
   * current selected subCategory
   */
  currentSubCategory: { id: number; name: string } = { id: 0, name: "" };

  /**
   * current selected subSubCategories
   */
  currentSubSubCategories: { id: number; name: string }[] = [];

  /**
   * to hold the current selected categories we're gonna filter the products by
   */
  selectedCategories: number[] = [];

  /**
   * list of the selected brands
   */
  selectedBrands: any[] = [];

  /**
   * the selected display state (null: every product, in stock or not)
   */
  selectedStock: "EN_STOCK" | "RUPTURE_DE_STOCK" | null = null;

  /**
   * object to unsubscribe
   */
  destroy$ = new Subject<void>();

  /**
   * filters object to diplay products list by
   */
  filter: {
    minPrice?: string;
    maxPrice?: string;
    brands?: string[];
    orderBy?: string;
    query?: string;
    inStock?: string;
    state?: string;
  } = {};

  /** list of available brands */
  brands: any[] = [];

  bank: ProjectTheme = "WUnivers"; //just for the colors in the UI

  /** Links for the breadcrumb navigation */
  breadcrumbsLinks: any;

  listMaxPrice: number = 1000000;
  listMinPrice!: number;

  /**
   * the products (categories, brands, search, stock) the slider bounds were computed for: the backend
   * `maxPrice` is the highest price of the returned page only, so within the same products it can only
   * raise the bound (a price filter, a sort or a page change must not shrink the slider)
   */
  private priceBoundsKey?: string;

  /** pending products request: a newer one cancels it, so an older response never overwrites the list */
  private productsSubscription?: Subscription;

  dataTotalElements: number = 0;
  pagination: { page: number; size: number } = {
    page: 1,
    size: 20,
  };

  paginationUpdated: boolean = true;

  get rowsLimit(): number {
    return this.pagination.size || 20;
  }

  listProductsLoaded: boolean = false;

  contactInformation?: ContactInformations;

  state: "loading" | "empty" | "error" | "default" = "loading";

  /** Static fallback image shown when a product has no image or it fails to load */
  readonly DEFAULT_PRODUCT_IMAGE = "assets/images/logo_placeholder.png";

  /** Subject for debouncing search input changes to avoid excessive API calls */
  searchQuerySubject = new BehaviorSubject<string>("");

  /** Controls visibility of the "product added to cart" confirmation popup */
  showAddPopup: boolean = false;

  /** Quantity displayed in the "product added to cart" popup */
  popQuantity: number = 1;

  /** Product configuration currently shown in the "added to cart" popup */
  popupConfiguration: ProductConfiguration | null = null;

  /** Available stock displayed in the "added to cart" popup */
  popupAvailableQuantity?: number;

  private readonly translate = inject(TranslateService);
  private readonly languageService = inject(LanguageService);

  /** Page-local snackbar (stock and cart messages) */
  readonly snackbar = new TimedSnackbar(this.translate);

  /** Flag to indicate if a product is being added to the cart */
  isAddingToCart: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: ActivatedRoute,
    private route: Router,
    private categoryService: CategoryService,
    private productService: ProductService,
    private productConfigurationService: ProductConfigurationService,
    private brandService: BrandService,
    private cdr: ChangeDetectorRef,
    private mensualPipe: MensualPipe,
    private accountService: AccountService,
    private cartService: CartService,
    private cartOffLineService: CartOffLineService,
    public favoriteService: FavoriteService,
    private seoService: SeoService,
  ) { }

  ngOnInit(): void {
    //initialize pagination

    // Debounce search input and re-query the filtered list (combines the
    // search query with the currently active category/brand/price filters
    // via ProductService.getByFilter).
    this.searchQuerySubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        //the query is already applied (e.g. cleared by clearSearch): no need to reload
        if (query === this.filter.query) return;
        this.filter = { ...this.filter, query };
        this.pagination = { ...this.pagination, page: 1 };
        this.getProductsList(this.filter, this.selectedCategories);
      });

    this.router.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      /**reintialize the list to not keep previous selected sub-subCategories from path*/
      this.currentSubSubCategories = [{ id: 0, name: "" }];

      /**
       * get the list of all available categories
       */
      this.categoryService
        .getAllActivated()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (categories: Category[]) => {
            //to be used in getting category name by it's id
            this.categoriesList = categories;

            //the list to display categories from
            this.structuredCategoriesList =
              this.getStrucutredCategories(categories);
            /**
             * initial categories from path
             */
            if (params.get("id")) {
              this.currentCategory = {
                id: Number(params.get("id")),
                name: this.getCategoryNameById(Number(params.get("id"))),
              };
            }

            if (params.get("category")) {
              this.currentSubCategory = {
                id: Number(params.get("category")),
                name: this.getCategoryNameById(Number(params.get("category"))),
              };
            }
            if (this.router.snapshot.queryParamMap.get("categories")) {
              const id = this.router.snapshot.queryParamMap.get("categories");
              if (
                id !== this.currentCategory.id.toString() &&
                id !== this.currentSubCategory.id.toString()
              ) {
                this.currentSubSubCategories.push({
                  id: Number(id),
                  name: this.getCategoryNameById(
                    Number(
                      this.router.snapshot.queryParamMap.get("categories"),
                    ),
                  ),
                });
              }
            }

            /**
             * set the list of categories to filter with
             */
            this.setCurrentSelectedCategories();

            this.updateBreadCrumbs();
            this.updateSeo();
            this.seoService.setBreadcrumbs(this.breadcrumbsLinks);

            /**
             * update the brands to display by selected category, sub-category or the list of selected-categories
             */
            this.getBrandsBySelectedCategories(
              this.selectedCategories,
              this.filter.inStock ?? "false",
            );

            //if it is a filter by brand (a brand is clicked in the header)
            if (params.get("brandId")) {
              const initialBrand = {
                id: Number(params.get("brandId")),
                imageUrl: "",
                name:
                  this.brands.find(
                    (item) => item.id === Number(params.get("brandId")),
                  )?.name ?? "",
              };
              this.selectedBrands.push(initialBrand);
            }

            /**initialize filters*/
            this.filter = {
              ...this.filter,
              inStock:
                this.router.snapshot.queryParamMap.get("inStock") ?? "false",
              brands: this.selectedBrands.map(
                (item) => item.id?.toString() ?? "",
              ),
            };
            this.getProductsList(this.filter, this.selectedCategories);
          },

          error: (error) => {
            console.error("error", error);
          },
        });
    });

    // the page is kept on language change (KEEP_ON_LANGUAGE_CHANGE): its data is reloaded with the same filters
    this.languageService.language$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.reloadInCurrentLanguage());

    this.router.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        const search = queryParams.get("search") ?? "";
        this.searchQuerySubject.next(search);
        this.productCatalogue?.onSearchInput(search);
      });
  }

  /**
   * function to structure the categories list to be used by the component
   * @param {Category[]} categories the list of all activated categories
   * @return {any[]} a list of structured categories depending on the component needed structure
   */
  getStrucutredCategories(categories: Category[]): any[] {
    const level1 = categories.filter((c) => !c.parentId);
    const level2 = categories.filter((c) =>
      level1.some((p) => p.id === c.parentId),
    );
    const level3 = categories.filter((c) =>
      level2.some((p) => p.id === c.parentId),
    );

    return level1.map((parent) => {
      const children = level2
        .filter((child) => child.parentId === parent.id)
        .map((child) => {
          const subChildren = level3
            .filter((sub) => sub.parentId === child.id)
            .map((sub) => ({ id: sub.id, name: sub.label }));

          const result: any = { id: child.id, name: child.label };
          if (subChildren.length) result.subSubcategory = subChildren;
          return result;
        });

      const result: any = { id: parent.id, category: parent.label };
      if (children.length) result.subcategory = children;
      return result;
    });
  }

  /**
   * load the products list for the selected category
   * @
   */

  getProductsList(
    filters: any,
    selectedCategories: number[],
    debounceDuration: number = 0, //used in case we're changing price values to not send APIs in a short time
  ) {
    this.productsSubscription?.unsubscribe();
    // wait before sending (the price slider emits on every step): a newer call cancels the waiting one
    this.productsSubscription = timer(debounceDuration)
      .pipe(
        switchMap(() =>
          this.productService.getByFilter(
            filters,
            selectedCategories,
            this.pagination.page - 1,
            this.pagination.size,
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response: any) => {
          this.productsList = response.page.content.map((product: any) => ({
            productId: product.id || null,
            images: [product.images[0]?.url || this.DEFAULT_PRODUCT_IMAGE],
            designation: product.name || null,
            description: this.stripHtml(product.description),
            reference: product.reference || null,
            brand: product.productBrand || null,
            category: product.categoryLabel || null,
            quantity: product.quantity ?? 0,
            stockStatus: product.state || null,
            salePrice: product.price || null,
            pricePerMonth: Math.round(
              this.mensualPipe.transform(product.price, 60),
            ),
          }));

          // Some products carry an image URL that is no longer reachable
          // (expired/forbidden S3 link). Verify each one client-side and
          // swap in the default image if it actually fails to load.
          this.verifyProductImages(this.productsList);

          this.updatePriceBounds(filters, selectedCategories, response.maxPrice);
          this.dataTotalElements = response.page.totalElements;
          this.state = this.productsList.length ? "default" : "empty";
        },
        error: (error: Error) => {
          console.error(error);
          this.state = "error";
        },
      });
  }

  /**
   * Sets the slider bounds: reset for new products (category, brand, search or stock change), only
   * raised otherwise
   * @param filters the filters of the request
   * @param selectedCategories the categories of the request
   * @param maxPrice the highest price of the returned page (null when it is empty)
   */
  private updatePriceBounds(
    filters: any,
    selectedCategories: number[],
    maxPrice?: number | null,
  ) {
    const key = JSON.stringify([
      selectedCategories,
      filters.brands,
      filters.query,
      filters.inStock,
      filters.state,
    ]);
    const isNewProducts = key !== this.priceBoundsKey;
    this.priceBoundsKey = key;
    this.listMinPrice = 0;
    if (isNewProducts) {
      this.listMaxPrice = maxPrice || 100000;
    } else if (maxPrice && maxPrice > this.listMaxPrice) {
      this.listMaxPrice = maxPrice;
    }
  }

  /**
   * The catalogue card renders `description` via text interpolation (not
   * [innerHTML]), but the value is HTML authored via the admin WYSIWYG
   * editor. Strip the markup here so raw tags don't show up in the card.
   */
  private stripHtml(html?: string): string {
    if (!html) {
      return "";
    }
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * Preloads each product's image and falls back to DEFAULT_PRODUCT_IMAGE
   * if it fails to load (e.g. expired/forbidden S3 link), since the API
   * returning a URL doesn't guarantee that URL is actually reachable.
   */
  private verifyProductImages(items: any[]): void {
    // no Image on the server: the check runs again in the browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    items
      .filter(
        (item) =>
          item.images?.[0] && item.images[0] !== this.DEFAULT_PRODUCT_IMAGE,
      )
      .forEach((item) => {
        const probe = new Image();
        probe.onerror = () => {
          item.images[0] = this.DEFAULT_PRODUCT_IMAGE;
        };
        probe.src = item.images[0];
      });
  }

  /**
   * Reloads the categories, brands and products in the new language, keeping the selected filters, sort,
   * search and page (the catalogue component isn't re-created, so its own state stays too)
   */
  private reloadInCurrentLanguage(): void {
    this.categoryService
      .getAllActivated()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categories: Category[]) => {
          this.categoriesList = categories;
          this.structuredCategoriesList = this.getStrucutredCategories(categories);
          //selected categories keep their id, with their name in the new language
          const rename = (category: { id: number; name: string }) =>
            category.id ? { ...category, name: this.getCategoryNameById(category.id) } : category;
          this.currentCategory = rename(this.currentCategory);
          this.currentSubCategory = rename(this.currentSubCategory);
          this.currentSubSubCategories = this.currentSubSubCategories.map(rename);

          this.updateBreadCrumbs();
          this.updateSeo();
          this.seoService.setBreadcrumbs(this.breadcrumbsLinks);
          this.getBrandsBySelectedCategories(
            this.selectedCategories,
            this.filter.inStock ?? "false",
          );
          this.getProductsList(this.filter, this.selectedCategories);
        },
        error: (error) => console.error(error),
      });
  }

  /**
   * get the name of the selected category/subCategory or sub-subCategory from the route params
   * @returns {number[]} the list of selected categories IDs*/

  getCategoryNameById(id: number): string {
    const category = this.categoriesList.find((cat) => cat.id === id);
    return category ? category.label : "";
  }

  /**
   * @description set the page title and description from the selected category and brand (category and brand
   * routes only, the other routes take theirs from the route data)
   */
  updateSeo() {
    if (!this.router.snapshot.data?.["seo"]?.dynamic) {
      return;
    }
    const brandId = this.router.snapshot.paramMap.get("brandId");
    if (!brandId) {
      this.setListSeo();
      return;
    }
    this.brandService
      .getById(Number(brandId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (brand: Brand) => this.setListSeo(brand.name),
        error: () => this.setListSeo(),
      });
  }

  /**
   * @description set the page title and description of a product list filtered by category and/or brand
   * @param {string} brandName name of the brand of a brand page
   */
  setListSeo(brandName?: string) {
    const categoryName =
      this.currentSubCategory.name || this.currentCategory.name;
    const name = [categoryName, brandName].filter(Boolean).join(" ");
    this.seoService.set({
      title: name || "Tous nos produits",
      description: name
        ? `${name} en cash ou par facilités de paiement sur Wissal Univers : grandes marques, offres exclusives et livraison dans toutes les wilayas.`
        : undefined,
    });
  }

  /**
   * @description update the breadcrumb links depending on the selected category, sub-category and sub-sub-categories to be used in the breadcrumb component
   */
  updateBreadCrumbs() {
    this.breadcrumbsLinks = [homeCrumb(this.translate)];

    if (this.currentCategory.id !== 0) {
      this.breadcrumbsLinks.push({
        label: this.currentCategory.name,
        url: `/products/category/${this.currentCategory.id}/${this.currentCategory.name}`,
      });
    }

    if (this.currentSubCategory.id !== 0) {
      this.breadcrumbsLinks.push({
        label: this.currentSubCategory.name,
        url: `/products/category/${this.currentCategory.id}/${this.currentCategory.name}/${this.currentSubCategory.id}/${this.currentSubCategory.name}`,
      });
    }

    // Only add subSubCategories if there are actual items with id !== 0
    const actualSubSubCategories = this.currentSubSubCategories.filter(
      (cat) => cat.id !== 0,
    );
    if (actualSubSubCategories.length === 1) {
      actualSubSubCategories.forEach((subSubCategory) => {
        this.breadcrumbsLinks.push({
          label: subSubCategory.name,
          url: `/products/category/${this.currentCategory.id}/${this.currentCategory.name}/${this.currentSubCategory.id}/${this.currentSubCategory.name}?categories=${subSubCategory.id}`,
        });
      });
    }
  }

  /**
   * reload products list with filtring on the new selected category
   * @param {{ category: { id: number; name: string } }} data the category selected by the user
   */

  onSelectCategory(data: { category: { id: number; name: string } }) {
    this.clearSearch();
    //update current selectedCategory
    this.currentCategory = data.category;
    this.selectedCategories = [this.currentCategory["id"]];
    //reset current selected sub-category and sub-sub-categories
    this.currentSubCategory = { id: 0, name: "" };
    this.currentSubSubCategories = [{ id: 0, name: "" }];
    /**update the list of brands*/
    this.getBrandsBySelectedCategories(
      this.selectedCategories,
      this.filter.inStock ?? "false",
    );
    //reset pagination
    this.pagination = {
      page: 1,
      size: 20,
    };
    //remove prices filters
    this.filter = { ...this.filter, minPrice: "", maxPrice: "" };
    this.paginationUpdated = true;
    //reload list of products filtred by the new selected category
    this.getProductsList(this.filter, this.selectedCategories);
    this.updateBreadCrumbs();
  }

  /**
   * reload products list with filtring on the new selected subCategory
   * @param { {  category: { id: number; name: string };  subcategory: { id: number; name: string };}} data category and subCatgory selected by the user
   */

  onSelectSubCategory(data: {
    category: { id: number; name: string };
    subcategory: { id: number; name: string };
  }) {
    this.clearSearch();
    //update current selected sub-category
    this.currentSubCategory = data.subcategory;
    //reset current selected sub-sub-categories
    this.currentSubSubCategories = [{ id: 0, name: "" }];
    //update the list of selected categories to filter by with the new selected sub-category
    this.selectedCategories = [this.currentSubCategory["id"]];
    /**update the list of brands*/
    this.getBrandsBySelectedCategories(
      this.selectedCategories,
      this.filter.inStock ?? "false",
    );
    //reset pagination
    this.pagination = {
      page: 1,
      size: 20,
    };
    //remove prices filters
    this.filter = { ...this.filter, minPrice: "", maxPrice: "" };

    this.getProductsList(this.filter, this.selectedCategories);
    this.updateBreadCrumbs();
    this.scrollToTheTop();
  }

  /**
   * reload products list with filtring on the new selected subCategory
   * @param { {category: { id: number; name: string };subcategory: { id: number; name: string };subsubcategories: { id: number; name: string }[];}} data category,subCatgory and list of subCategories selected by the user
   */
  onSelectSubSubCategory(data: {
    category: { id: number; name: string };
    subcategory: { id: number; name: string };
    subsubcategories: { id: number; name: string }[];
  }) {
    this.clearSearch();
    //update list of current selected sub-subCategories
    this.currentSubSubCategories = data.subsubcategories ?? [];
    this.selectedCategories =
      this.currentSubSubCategories?.map((item) => item.id) || [];
    /**update the list of brands*/
    this.getBrandsBySelectedCategories(
      this.selectedCategories,
      this.filter.inStock ?? "false",
    );
    //reset pagination
    this.pagination = {
      page: 1,
      size: 20,
    };
    //remove prices filters
    this.filter = { ...this.filter, minPrice: "", maxPrice: "" };
    this.getProductsList(this.filter, this.selectedCategories);
    this.updateBreadCrumbs();
    this.scrollToTheTop();
  }

  /**
   * select a brand and reload the updated products list by new filters
   * @param {{ id: number; name: string }} data the selected brand
   */
  onSelectBrand(data: { brands: { id: number; name: string }[] }) {
    this.clearSearch();
    this.selectedBrands = [];
    //add the new selected brand to list of previous selected once
    this.selectedBrands = data.brands;

    //update filter to unclude new selected brand
    this.filter = {
      ...this.filter,
      brands: this.selectedBrands.map((item) => item.id?.toString() ?? ""),
    };
    //reset pagination
    this.pagination = {
      page: 1,
      size: 20,
    };
    //remove prices filters
    this.filter = { ...this.filter, minPrice: "", maxPrice: "" };
    //reload products with new filters
    this.getProductsList(this.filter, this.selectedCategories);
    this.scrollToTheTop();
  }

  /**
   * select a display state and reload the updated products list by new filters
   * @param {{ stock: "EN_STOCK" | "RUPTURE_DE_STOCK" | null }} data the selected state, null when the filter is cleared
   */
  onSelectStock(data: { stock: "EN_STOCK" | "RUPTURE_DE_STOCK" | null }) {
    this.clearSearch();
    this.selectedStock = data.stock;
    //update filter with the new selected state
    this.filter = { ...this.filter, state: data.stock ?? "" };
    //reset pagination
    this.pagination = {
      page: 1,
      size: 20,
    };
    //reload products with new filters
    this.getProductsList(this.filter, this.selectedCategories);
    this.scrollToTheTop();
  }

  /**
   * reload the products list filtred by the new selected min price
   * @param {{minValue: number}} data the new price value
   */

  onMinPriceSelected(data: { minValue: number; maxValue: number }) {
    this.clearSearch();
    this.minPrice = data.minValue;
    //update filter with the new min price selected
    this.filter = {
      ...this.filter,
      minPrice: data.minValue.toString(),
      maxPrice: data.maxValue.toString(),
    };
    //reset pagination
    this.paginationUpdated = false;
    //reload products list
    this.getProductsList(this.filter, this.selectedCategories, 300);
  }

  /**
   * reload the products list filtred by the new selected max price
   * @param {{maxValue: number}} data the new price value
   */

  onMaxPriceSelected(data: { minValue: number; maxValue: number }) {
    this.clearSearch();
    this.maxPrice = data.maxValue;
    //update filter with the new min price selected
    this.filter = {
      ...this.filter,
      minPrice: data.minValue.toString(),
      maxPrice: data.maxValue.toString(),
    };
    //reset pagination
    this.paginationUpdated = false;
    //reload products list
    this.getProductsList(this.filter, this.selectedCategories, 300);
  }

  /**
   * navigate to the selected product details page
   * @param {string} productId selected product id
   */
  navigateToProductsDetails(productId: string) {
    this.productConfigurationService
      .getByConfigurationId(Number(productId))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (configuration: any) => {
          const selectedproduct = configuration;
          const safeName = selectedproduct?.name?.replace(/\//g, "-");

          this.route.navigate([
            `/products/${safeName}/${selectedproduct.productId}/${productId}`,
          ]);
        },
        (err) => {
          console.error(err);
        },
      );
  }

  /**
   * set the list of selected categories according to current selected category, sub category or sub-sub category
   */

  setCurrentSelectedCategories(): void {
    this.selectedCategories = [];
    if (
      this.currentSubSubCategories.length > 0 &&
      !(
        this.currentSubSubCategories.length === 1 &&
        this.currentSubSubCategories[0].id === 0
      )
    ) {
      this.selectedCategories = this.currentSubSubCategories.map(
        (item) => item.id,
      );
    } else if (this.currentSubCategory.id !== 0) {
      this.selectedCategories.push(this.currentSubCategory.id);
    } else if (this.currentCategory.id !== 0) {
      this.selectedCategories.push(this.currentCategory.id);
    }
  }

  /**retrieve the list of brands filtred by selected categories
   * @param {number[]} categories list of curret selected categories IDs
   * @param {string} inStock if true retrieve just brands in stock else all
   */
  getBrandsBySelectedCategories(categories: number[], inStock: string): void {
    if (categories.length > 0) {
      this.brandService.getByCategoryId(categories, inStock).subscribe({
        next: (response: Brand[]) => {
          this.brands = response;
        },
        error: (error) => {
          console.log(error);
        },
      });
    } else {
      this.brandService.getAllPaged(0, 10).subscribe({
        next: (brandsList) => {
          this.brands = brandsList.content;
        },
        error: (err) => {
          console.error(err);
        },
      });
    }
  }

  /**
   * @description update the queryParams + update the UI
   * @param {QueryOptions} data the new queryParams
   */
  onQueryParamsChange(data: any) {
    this.pagination.page = data.pagination.page;
    this.pagination.size = data.pagination.size;
    //this.cdr.detectChanges();
    this.getProductsList(this.filter, this.selectedCategories);
    this.scrollToTheTop();
  }

  /**
   * @description contact wissal univers client service
   */
  contactClientService() {
    window.location.href = "tel:" + this.contactInformation?.wissalPhoneNumber;
  }

  /**
   * @description order products list according to the selected order type by price
   */
  onOrderPriceUpdated(order: string) {
    this.filter = {
      ...this.filter,
      orderBy: order,
    };
    this.getProductsList(this.filter, this.selectedCategories);
  }

  /**
   * scroll to the top of the page for a better UX
   */
  private scrollToTheTop(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  }

  /**
   * @description a new filter is chosen: empty the searched text in the catalogue search bar,
   * remove it from the products query and from the url (the header empties its search bar on that)
   */
  private clearSearch(): void {
    if (this.filter.query || this.searchQuerySubject.value) {
      this.filter = { ...this.filter, query: "" };
      this.productCatalogue?.onSearchInput("");
    }
    if (this.router.snapshot.queryParamMap.get("search")) {
      this.route.navigate([], {
        relativeTo: this.router,
        queryParams: { search: null },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
    }
  }

  /**
   * Handles the search input emitted by the product catalogue.
   * Debounces keystrokes and pushes the query through to getProductsList,
   * which combines it with the currently active category/brand/price filters.
   * @param query - Current value of the search input
   */
  onSearch(query: string): void {
    this.searchQuerySubject.next(query ?? "");
  }

  /** Adds/removes the clicked product configuration from the favorites */
  onToggleWishlist(configurationId: number): void {
    this.favoriteService.toggle(configurationId).subscribe();
  }

  /**
   * Handles the add-to-cart event emitted by the product catalogue.
   * Fetches the full configuration then delegates to addConfigurationToCart.
   * @param {{ id: number; productConfigId?: number }} event - Product id and selected configuration id
   */
  onAddToCart(event: { id: number; productConfigId?: number }): void {
    const configurationId = event.productConfigId ?? event.id;
    if (!configurationId) {
      return;
    }

    this.isAddingToCart = true;

    this.productConfigurationService
      .getByConfigurationId(configurationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (configuration: ProductConfiguration) => {
          this.isAddingToCart = true;
          this.addConfigurationToCart(configuration);
        },
        error: () => {
          this.isAddingToCart = true;
          this.snackbar.show("alert", "productsPage.snackbar.genericError");
        },
      });
  }

  /**
   * Adds a product configuration to the cart (online or offline), same logic as
   * DetailPageComponent.addToCart.
   * @param configuration - The product configuration to add
   */
  private addConfigurationToCart(configuration: ProductConfiguration): void {
    const quantity = 1;

    if (this.accountService.accountSubject.value) {
      // Online storage
      this.cartService.add(configuration, quantity).subscribe({
        next: (response: any) => {
          this.popupConfiguration = configuration;
          this.popupAvailableQuantity = configuration.quantity;
          this.popQuantity = response?.cartProduct?.quantity || quantity;
          this.showAddPopup = true;

          if (response?.quantityCapped) {
            this.snackbar.show(
              "warning",
              "productsPage.snackbar.quantityExceedsStock",
              { quantity: this.popQuantity },
            );
          }
        },
        error: () => {
          this.snackbar.show("alert", "productsPage.snackbar.addToCartError");
        },
      });
    } else {
      // Offline storage
      const cartProduct: CartProduct = {
        configuration,
        quantity,
        configurationId: configuration.id,
      };

      this.cartOffLineService.addCarts(cartProduct).subscribe({
        next: (response) => {
          if (response.success) {
            this.cartOffLineService.cart
              .pipe(take(1))
              .subscribe((res: cartLocal) => {
                const existingProduct = res?.CartProduct?.find(
                  (c: any) =>
                    c.configuration.id === cartProduct.configurationId,
                );
                this.popupConfiguration = configuration;
                this.popupAvailableQuantity = configuration.quantity;
                this.popQuantity = existingProduct?.quantity ?? quantity;
                this.showAddPopup = true;
              });
          }
          if (response.quantityCapped) {
            this.snackbar.show("warning", "productsPage.snackbar.stockExceeded");
          }
        },
        error: () => {
          this.snackbar.show("alert", "productsPage.snackbar.addToCartError");
        },
      });
    }
  }

  /**
   * Updates the "added to cart" popup visibility state.
   * @param showPopup - Boolean to show or hide the popup
   */
  onShowAddPopupChange(showPopup: boolean): void {
    this.showAddPopup = showPopup;
  }

  /**
   * Navigates to the cart page.
   */
  onSeeCart(): void {
    this.route.navigate(["/cart"]);
  }

  /**
   * Closes the "added to cart" popup so the user can keep browsing the catalogue.
   */
  onContinueShopping(): void {
    this.showAddPopup = false;
  }

  /**emit a value in destroy observable so we unsubscribe from all the observables we're subscribed to
   */
  ngOnDestroy() {
    this.seoService.removeJsonLd("breadcrumbs");
    this.destroy$.next();
    this.destroy$.complete();
    this.searchQuerySubject.complete();
  }
}
